<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import UiButton from '@/Components/UI/UiButton.vue'
import UiTextarea from '@/Components/UI/UiTextarea.vue'
import UiMenu from '@/Components/UI/UiMenu.vue'
import Icon from '@/Components/UI/Icon.vue'
import { useInboxStore } from '@/stores/useInboxStore'

const props = defineProps({
  conversationId: { type: Number, required: true },
  taken:          { type: Boolean, default: false },
})

const { t } = useI18n()
const store = useInboxStore()
const content = ref('')
const sending = ref(false)
const error   = ref(null)
const fileInput = ref(null)
const pendingAttachment = ref(null)
const attachmentError = ref(null)

const quickReplies = computed(() => store.quickReplies)

const ALLOWED_TYPES = [ 'application/pdf', 'image/jpeg', 'image/png', 'image/gif', 'image/webp' ]
const MAX_BYTES = 10 * 1024 * 1024

const insertQuickReply = (body) => {
  content.value = content.value ? `${content.value}\n${body}` : body
}

const pickFile = () => fileInput.value?.click()

const onFileChosen = (e) => {
  attachmentError.value = null
  const f = e.target.files?.[0]
  if (!f) return
  const allowedExact = ALLOWED_TYPES.includes(f.type)
  const allowedImage = typeof f.type === 'string' && f.type.startsWith('image/')
  if (!allowedExact && !allowedImage) {
    attachmentError.value = t('inbox.composer.attachmentBad')
    e.target.value = ''
    return
  }
  if (f.size > MAX_BYTES) {
    attachmentError.value = t('inbox.composer.attachmentTooBig')
    e.target.value = ''
    return
  }
  pendingAttachment.value = f
  e.target.value = ''
}

const clearAttachment = () => { pendingAttachment.value = null; attachmentError.value = null }

const fileSizeLabel = (bytes) => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content || ''

// ── Vehicle link picker ──────────────────────────────────────
const vehiclePickerOpen = ref(false)
const vehicleQuery = ref('')
const vehicleResults = ref([])
const vehicleSending = ref(false)
let searchTimer = null

const openVehiclePicker = async () => {
  vehiclePickerOpen.value = true
  await loadVehicles('')
}

const loadVehicles = async (q) => {
  try {
    const res = await fetch(`/vehicles.json?q=${encodeURIComponent(q || '')}`, {
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    })
    if (!res.ok) return
    vehicleResults.value = await res.json()
  } catch { /* ignore */ }
}

const onVehicleSearch = () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => loadVehicles(vehicleQuery.value), 200)
}

const sendVehicleLink = async (vehicle) => {
  if (vehicleSending.value) return
  vehicleSending.value = true
  try {
    const fd = new FormData()
    fd.append('vehicle_id', String(vehicle.id))
    const res = await fetch(`/inbox/conversations/${props.conversationId}/share_vehicle_link`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken(),
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: fd,
    })
    if (!res.ok) {
      error.value = t('inbox.thread.sendFailed')
    } else {
      vehiclePickerOpen.value = false
      vehicleQuery.value = ''
    }
  } finally {
    vehicleSending.value = false
  }
}

const send = async () => {
  if ((!content.value.trim() && !pendingAttachment.value) || sending.value) return
  sending.value = true
  error.value = null
  try {
    const result = await store.sendMessage({
      content: content.value,
      attachment: pendingAttachment.value,
    })
    if (!result) {
      error.value = t('inbox.thread.sendFailed')
      return
    }
    content.value = ''
    pendingAttachment.value = null
  } finally {
    sending.value = false
  }
}
</script>

<template>
  <div class="border-t border-[color:var(--line)] p-3 bg-[color:var(--surface)]">
    <div
      v-if="!props.taken"
      class="text-xs text-[color:var(--muted)] py-2 text-center"
    >
      {{ t('inbox.thread.takeToInteract') }}
    </div>
    <template v-else>
      <div v-if="error" class="mb-2 text-xs text-[color:var(--danger)]">{{ error }}</div>
      <div v-if="attachmentError" class="mb-2 text-xs text-[color:var(--danger)]">{{ attachmentError }}</div>
      <div v-if="pendingAttachment"
           class="mb-2 inline-flex items-center gap-2 px-2 py-1 rounded-[var(--radius-md)] bg-[color:var(--surfaceAlt)] text-xs">
        <Icon name="paperclip" :size="14" />
        <span class="fw-value text-[color:var(--ink)]">{{ pendingAttachment.name }}</span>
        <span class="text-[color:var(--muted)]">{{ fileSizeLabel(pendingAttachment.size) }}</span>
        <button type="button" class="text-[color:var(--muted)] hover:text-[color:var(--danger)]" @click="clearAttachment">×</button>
      </div>
      <input ref="fileInput" type="file" accept="application/pdf,image/*" class="hidden" @change="onFileChosen" />
      <form @submit.prevent="send" class="flex items-end gap-2">
        <UiTextarea
          v-model="content"
          :placeholder="t('inbox.thread.placeholder')"
          rows="2"
          class="flex-1"
          @keydown.enter.exact.prevent="send"
        />
        <UiButton variant="secondary" size="md"
                  :title="t('inbox.composer.attach')"
                  :aria-label="t('inbox.composer.attach')"
                  @click="pickFile">
          <Icon name="paperclip" :size="16" />
        </UiButton>
        <UiButton variant="secondary" size="md"
                  :title="t('inbox.composer.sendVehicleLink')"
                  :aria-label="t('inbox.composer.sendVehicleLink')"
                  @click="openVehiclePicker">
          <Icon name="fleet" :size="16" />
        </UiButton>
        <UiMenu v-if="quickReplies.length">
          <template #trigger="{ toggle }">
            <UiButton variant="secondary" size="md"
                      :title="t('inbox.composer.quickReply')"
                      :aria-label="t('inbox.composer.quickReply')"
                      @click="toggle">
              <Icon name="bolt" :size="16" />
            </UiButton>
          </template>
          <button v-for="r in quickReplies" :key="r.id"
                  type="button"
                  class="block w-full text-left px-3 py-2 hover:bg-[color:var(--surfaceAlt)]"
                  @click="insertQuickReply(r.body)">
            <span class="text-sm fw-value">{{ r.label }}</span>
            <span class="block text-xs text-[color:var(--muted)] truncate mt-0.5">{{ r.body }}</span>
          </button>
        </UiMenu>
        <UiButton type="submit" variant="primary" :loading="sending" :disabled="!content.trim() && !pendingAttachment">
          {{ sending ? t('inbox.thread.sending') : t('inbox.thread.send') }}
        </UiButton>
      </form>
    </template>

    <!-- Vehicle link picker -->
    <div
      v-if="vehiclePickerOpen"
      class="fixed inset-0 z-50 flex md:items-center md:justify-center md:p-4 bg-black/60"
      @click.self="vehiclePickerOpen = false"
    >
      <div
        class="w-full h-full md:h-auto md:max-w-md md:rounded-[var(--radius-lg)] md:shadow-[var(--shadow-lg)] bg-[color:var(--surface)] flex flex-col md:max-h-[calc(100vh-2rem)]"
      >
        <header class="flex items-center justify-between p-4 border-b border-[color:var(--line)] flex-shrink-0">
          <h3 class="font-display text-base text-[color:var(--ink)]">
            {{ t('inbox.composer.sendVehicleLink') }}
          </h3>
          <button
            type="button"
            class="text-[color:var(--muted)] hover:text-[color:var(--ink)]"
            :aria-label="t('common.close')"
            @click="vehiclePickerOpen = false"
          >
            <Icon name="x" :size="20" />
          </button>
        </header>
        <div class="p-4 flex-shrink-0">
          <input
            v-model="vehicleQuery"
            type="search"
            :placeholder="t('inbox.composer.vehicleSearch')"
            class="w-full px-3 py-2 rounded-[var(--radius-input)] border border-[color:var(--line)] bg-[color:var(--surface)] text-sm text-[color:var(--ink)] focus:outline-2 focus:outline-[color:var(--accent)]"
            @input="onVehicleSearch"
          >
        </div>
        <ul
          v-if="vehicleResults.length"
          role="list"
          class="flex-1 overflow-y-auto divide-y divide-[color:var(--line)]"
        >
          <li v-for="v in vehicleResults" :key="v.id">
            <button
              type="button"
              class="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-[color:var(--surfaceAlt)] disabled:opacity-50"
              :disabled="vehicleSending"
              @click="sendVehicleLink(v)"
            >
              <div class="w-10 h-10 rounded-md overflow-hidden bg-[color:var(--surfaceAlt)] flex items-center justify-center flex-shrink-0">
                <img v-if="v.photo_url" :src="v.photo_url" alt="" class="w-full h-full object-cover">
                <Icon v-else name="fleet" :size="18" class="text-[color:var(--muted)]" />
              </div>
              <div class="flex-1 min-w-0">
                <div class="text-sm fw-value text-[color:var(--ink)] truncate">{{ v.make }} {{ v.model }}</div>
                <div class="text-xs text-[color:var(--muted)] font-mono">{{ v.registration }}</div>
              </div>
            </button>
          </li>
        </ul>
        <p v-else class="px-4 py-6 text-sm text-[color:var(--muted)] text-center">
          {{ t('inbox.composer.noVehicles') }}
        </p>
      </div>
    </div>
  </div>
</template>
